import { useState, useEffect, useRef } from "react";
import { Loader2, Volume2 } from "lucide-react";
import { cn } from "@repo/utils";
import Typewriter from "typewriter-effect";
import type { MessageRole } from "../../lib/types";

interface ChatBubbleProps {
  role: MessageRole;
  content: string;
  isLatest?: boolean;
  shouldTypeWrite?: boolean;
  onTypewriterComplete?: () => void;
  onVocalize?: () => void;
  audioStatus?: "idle" | "loading" | "playing" | "ready";
}

export function ChatBubble({
  role,
  content,
  isLatest = false,
  shouldTypeWrite = false,
  onTypewriterComplete,
  onVocalize,
  audioStatus = "idle",
}: ChatBubbleProps) {
  const isAssistant = role === "assistant";
  const [isTypingComplete, setIsTypingComplete] = useState(false);

  // Ref stable pour que l'effet ci-dessous ne se rejoue pas quand la référence
  // du callback change — elle est recréée à chaque rendu du parent.
  const onTypewriterCompleteRef = useRef(onTypewriterComplete);
  onTypewriterCompleteRef.current = onTypewriterComplete;

  // Arrête l'intervalle de défilement à la fin de l'effet machine à écrire
  const handleTypewriterComplete = () => {
    setIsTypingComplete(true);
    onTypewriterCompleteRef.current?.();
  };

  // Réinitialise l'état « frappe terminée » quand le contenu change.
  // En affichage statique (sans machine à écrire), notifie dès le montage.
  useEffect(() => {
    if (shouldTypeWrite && isAssistant) {
      setIsTypingComplete(false);
    } else if (!shouldTypeWrite && isAssistant) {
      onTypewriterCompleteRef.current?.();
    }
  }, [content, shouldTypeWrite, isAssistant]);

  return (
    <div
      className={cn(
        "flex w-full",
        isAssistant ? "justify-start" : "justify-end",
        isLatest && "animate-in slide-in-from-bottom-2 duration-300",
      )}
    >
      <div
        className={cn(
          "max-w-[80%] rounded-2xl px-4 shadow-sm",
          isAssistant
            ? "bg-muted text-foreground rounded-bl-md pt-3"
            : "bg-primary text-primary-foreground rounded-br-md py-3",
          isLatest && "animate-in fade-in duration-200",
        )}
        style={isAssistant ? { paddingBottom: "1.625em" } : undefined}
      >
        <div
          className="text-sm md:text-base whitespace-pre-wrap"
          style={{ lineHeight: "1.625" }}
        >
          {shouldTypeWrite && isAssistant && isLatest ? (
            <div style={{ lineHeight: "1.625", minHeight: "1.625em" }}>
              <style>{`
                .typing-cursor-hidden .Typewriter__cursor {
                  display: none !important;
                }
              `}</style>
              <div className={isTypingComplete ? "typing-cursor-hidden" : ""}>
                <Typewriter
                  onInit={(typewriter) => {
                    typewriter
                      .changeDelay(15)
                      .typeString(content)
                      .callFunction(() => {
                        handleTypewriterComplete();
                      })
                      .start();
                  }}
                  options={{
                    cursor: "|",
                    delay: 15,
                    cursorClassName:
                      "Typewriter__cursor animate-pulse font-normal",
                  }}
                />
              </div>
            </div>
          ) : (
            <span
              style={{
                lineHeight: "1.625",
                display: "inline-block",
                width: "100%",
              }}
            >
              {content}
            </span>
          )}
        </div>
        {isAssistant && onVocalize && (
          <div className="flex justify-end mt-1">
            <button
              onClick={onVocalize}
              disabled={audioStatus === "loading"}
              title={
                audioStatus === "loading"
                  ? "Chargement..."
                  : audioStatus === "playing"
                    ? "Lecture en cours"
                    : audioStatus === "ready"
                      ? "Réécouter"
                      : "Écouter le message"
              }
              className={cn(
                "p-1 rounded-full transition-opacity",
                audioStatus === "playing"
                  ? "opacity-100 text-primary"
                  : "opacity-30 hover:opacity-70",
                audioStatus === "loading" && "cursor-not-allowed",
              )}
            >
              {audioStatus === "loading" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Volume2 className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
